import { useEffect } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';

import type { useClaimStartingCoinsMutation } from 'ms-helpers/coins/__generated__/useClaimStartingCoinsMutation.graphql';
import useFeatureFlagsV2 from 'ms-helpers/useFeatureFlagsV2';
import { Logger } from 'ms-utils/app-logging';
import type { FixGeneratedKey } from 'ms-utils/relay/relay-compiler-language-typescript-fixes';

import type { useClaimStartingCoinsFragment$key } from './__generated__/useClaimStartingCoinsFragment.graphql';

/**
 * This hook is used to claim the starting coins for a student.
 * It checks if the student has already claimed their starting coins,
 * and if not, it triggers the mutation to claim them.
 *
 * @param studentKey - The fragment key for the student data.
 * @returns An object containing whether the student has claimed their starting coins
 *          and whether the claim mutation is in progress.
 */
export const useClaimStartingCoins = (
  studentKey: FixGeneratedKey<useClaimStartingCoinsFragment$key>,
) => {
  const storeState = useFragment(
    graphql`
      fragment useClaimStartingCoinsFragment on Student
      @argumentDefinitions(enableShop: { type: "Boolean!" }) {
        gamification @include(if: $enableShop) {
          hasClaimedStartingCoins
        }
      }
    `,
    studentKey,
  );

  const hasClaimedStartingCoins =
    storeState.gamification?.hasClaimedStartingCoins;

  const { gamificationEnableShop } = useFeatureFlagsV2();

  const [claimStartingCoins, isClaimingStartingCoins] =
    useMutation<useClaimStartingCoinsMutation>(graphql`
      mutation useClaimStartingCoinsMutation {
        claimStartingCoins {
          errors {
            key
            message
          }
          student {
            gamification {
              hasClaimedStartingCoins
              coinsBalance
            }
          }
        }
      }
    `);

  useEffect(() => {
    // Only submit the claim mutation if the field in the store is explicitly set to false.
    // Skip if it's null or true, or if the feature flag is disabled.
    if (
      gamificationEnableShop &&
      hasClaimedStartingCoins != null &&
      !hasClaimedStartingCoins
    ) {
      claimStartingCoins({
        variables: {},
        // For now, we don't want to update the UI if the mutation is successful, as we are just silently
        // and automatically claiming the coins upon visiting any page with the navbar.
        // So we just log errors to Sentry if any.
        onCompleted: ({ claimStartingCoins: response }) => {
          if (response.errors.length > 0) {
            Logger.error(
              'GraphQL errors returned when claiming starting bonus coins',
              {
                extra: {
                  errors: response.errors.map(({ key }) => key).join(', '),
                },
              },
            );
            return;
          }
        },
        onError: error => {
          Logger.error('Query error when claiming starting bonus coins', {
            extra: { error: error.message },
          });
        },
      });
    }
  }, [gamificationEnableShop, hasClaimedStartingCoins, claimStartingCoins]);

  return { hasClaimedStartingCoins, isClaimingStartingCoins };
};
